import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, ArrowLeft, BookOpen, Shield, FileText, 
  AlertCircle, CheckCircle, Info, Loader2, HelpCircle, Building,
  FolderOpen, ExternalLink, Lightbulb
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SafetyAssistant = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [topics, setTopics] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadTopics();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTopics = async () => {
    try {
      const response = await axiosInstance.get('/safety-assistant/topics');
      setTopics(response.data);
    } catch (error) {
      console.error('Failed to load topics:', error);
    }
  };

  const handleAskQuestion = async (e) => {
    e?.preventDefault();
    if (!question.trim() || isLoading) return;

    const userQuestion = question.trim();
    setQuestion('');
    
    // Add user message to conversation
    setConversation(prev => [...prev, {
      type: 'user',
      content: userQuestion,
      timestamp: new Date().toISOString()
    }]);

    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/safety-assistant/ask', {
        question: userQuestion
      });

      // Add AI response to conversation
      setConversation(prev => [...prev, {
        type: 'assistant',
        content: response.data.answer,
        source: response.data.source,
        sourceName: response.data.source_name,
        oshaReference: typeof response.data.osha_reference === 'object' 
          ? response.data.osha_reference 
          : { citation: response.data.osha_reference || response.data.osha_citation },
        oshaTopic: response.data.osha_topic,
        confidence: response.data.confidence,
        excerpt: response.data.relevant_excerpt,
        note: response.data.note,
        suggestions: response.data.suggestions,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Failed to get answer:', error);
      toast.error(t('safetyAssistant.failedAnswer'));
      setConversation(prev => [...prev, {
        type: 'error',
        content: 'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (q) => {
    setQuestion(q);
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'division_manual': return <FolderOpen className="w-4 h-4" />;
      case 'company_manual': return <Building className="w-4 h-4" />;
      case 'company_policies': return <FileText className="w-4 h-4" />;
      case 'osha': return <Shield className="w-4 h-4" />;
      case 'ai_knowledge': return <Lightbulb className="w-4 h-4" />;
      default: return <HelpCircle className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'division_manual': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'company_manual': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'company_policies': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'osha': return 'bg-green-100 text-green-700 border-green-200';
      case 'ai_knowledge': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high': return <Badge className="bg-green-100 text-green-700">{t('safetyAssistant.highConfidence')}</Badge>;
      case 'medium': return <Badge className="bg-amber-100 text-amber-700">{t('safetyAssistant.mediumConfidence')}</Badge>;
      case 'low': return <Badge className="bg-red-100 text-red-700">{t('safetyAssistant.lowConfidence')}</Badge>;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('safetyAssistant.title')}</h1>
              <p className="text-gray-600">{t('safetyAssistant.askAnyQuestion')}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Main Chat Area */}
          <div className="lg:col-span-3 space-y-4">
            {/* Chat Messages */}
            <Card className="min-h-[500px] flex flex-col">
              <CardContent className="flex-1 p-4 overflow-y-auto max-h-[600px]">
                {conversation.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <MessageCircle className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Ask a Safety Question
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-md">
                      I&apos;ll search your division safety manual, company safety manual, and OSHA regulations to find the best answer.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {topics?.example_questions?.slice(0, 4).map((q, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickQuestion(q)}
                          className="text-xs"
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversation.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.type === 'user' ? (
                          <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                            <p>{msg.content}</p>
                          </div>
                        ) : msg.type === 'error' ? (
                          <div className="bg-red-50 border border-red-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                            <div className="flex items-center gap-2 text-red-600">
                              <AlertCircle className="w-4 h-4" />
                              <p>{msg.content}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] shadow-sm">
                            {/* Source Badge */}
                            {msg.source && msg.source !== 'none' && (
                              <div className="flex items-center gap-2 mb-3">
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getSourceColor(msg.source)}`}>
                                  {getSourceIcon(msg.source)}
                                  <span>{msg.sourceName || msg.source.replace('_', ' ')}</span>
                                </div>
                                {getConfidenceBadge(msg.confidence)}
                              </div>
                            )}
                            
                            {/* Answer */}
                            <div className="prose prose-sm max-w-none">
                              <p className="text-gray-800 whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            
                            {/* OSHA Reference */}
                            {msg.oshaReference && (msg.oshaReference.citation || msg.oshaReference.topic) && (
                              <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-100">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                                    <Shield className="w-4 h-4" />
                                    OSHA Reference: {msg.oshaReference.citation || 'General OSHA guidance'}
                                    {msg.oshaReference.topic && <span className="text-green-600">({msg.oshaReference.topic})</span>}
                                  </div>
                                  {msg.oshaReference.summary && (
                                    <p className="text-xs text-green-600 ml-6">{msg.oshaReference.summary}</p>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Relevant Excerpt */}
                            {msg.excerpt && (
                              <details className="mt-3">
                                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                  View source excerpt
                                </summary>
                                <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 max-h-32 overflow-y-auto">
                                  {msg.excerpt}
                                </div>
                              </details>
                            )}
                            
                            {/* Note */}
                            {msg.note && (
                              <div className="mt-3 flex items-start gap-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>{msg.note}</span>
                              </div>
                            )}
                            
                            {/* Suggestions */}
                            {msg.suggestions && msg.suggestions.length > 0 && (
                              <div className="mt-3 p-2 bg-gray-50 rounded">
                                <p className="text-xs font-medium text-gray-500 mb-1">Suggestions:</p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                  {msg.suggestions.map((s, i) => (
                                    <li key={i} className="flex items-center gap-2">
                                      <CheckCircle className="w-3 h-3 text-gray-400" />
                                      {s}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                          <div className="flex items-center gap-2 text-gray-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>{t('safetyAssistant.searchingManuals')}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </CardContent>
              
              {/* Input Area */}
              <div className="border-t p-4">
                <form onSubmit={handleAskQuestion} className="flex gap-2">
                  <Input
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={t('safetyAssistant.askQuestion')}
                    disabled={isLoading}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={!question.trim() || isLoading}>
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </Card>
          </div>

          {/* Side Panel - Topics */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Safety Topics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {topics?.topics?.slice(0, 8).map((topic, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickQuestion(`What are the safety requirements for ${topic.topic.toLowerCase()}?`)}
                    className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                  >
                    <Shield className="w-3 h-3 text-gray-400" />
                    {topic.topic}
                  </button>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Search Priority
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs text-gray-600">
                <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
                  <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</span>
                  Division Safety Manual
                </div>
                <div className="flex items-center gap-2 p-2 bg-purple-50 rounded">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs">2</span>
                  Company Safety Manual
                </div>
                <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
                  <span className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center text-xs">3</span>
                  OSHA Regulations
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SafetyAssistant;
